import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { fetchReportShelterIntakesExits } from "../../../services/httpClient";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  exit_at: string;
  exit_note_id: string;
  exit_type: string;
}

interface ExitTypeChartProps {
  data?: RawDataItem[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
}

export const ShelterExitTypePieChart = ({ data, startDate, endDate, useDatePicker = false }: ExitTypeChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchedData, setFetchedData] = useState<RawDataItem[]>([]);

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      if (data) {
        if (startDate && endDate) {
          const exits = data.filter((item: any) => {
            const exitDate = new Date(item.exit_at);
            return exitDate >= new Date(startDate) && exitDate <= new Date(endDate);
          });
          setFetchedData(exits);
        } else {
          setFetchedData(data);
        }
        setIsLoading(false);
      } else {
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);
        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }

        await getExitData(start, end);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [data, startDate, endDate, useDatePicker]);

  const getExitData = async (start: string, end: string) => {
    await fetchReportShelterIntakesExits()
      .then((res: any) => {
        const data = res[0].data;
        const exitFiltered = data.filter((item: any) => item.exit_at);
        let exits = exitFiltered.map((item: any) => ({
          exit_at: item.exit_at,
          exit_note_id: item.exit_note_id,
          exit_type: item.exit_type,
        }));
        exits = exits.filter((item: any) => {
          const exitDate = new Date(item.exit_at);
          return exitDate >= new Date(start) && exitDate <= new Date(end);
        });
        setFetchedData(exits);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const typeGroups = groupByExitType(fetchedData).sort((a, b) => {
    if (a.label === "NONE") return 1;
    if (b.label === "NONE") return -1;
    return a.label.localeCompare(b.label);
  });

  function groupByExitType(data: RawDataItem[]) {
    const counts: Record<string, { name: string; count: number }> = {};
    data.forEach((item) => {
      const key = item.exit_type;
      const name = item.exit_type ? item.exit_type.toUpperCase() : "NONE";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts).map(([id, { name, count }]) => ({ id, label: name, numberCases: count }));
  }

  const chartData = {
    labels: typeGroups.length > 0 ? typeGroups.map((item) => item.label) : ["No data available"],
    datasets: [
      {
        label: "# People",
        data: typeGroups.length > 0 ? typeGroups.map((item) => item.numberCases) : [0],
        backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      title: {
        display: true,
        text: "Exits By Type",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "left" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Pie data={chartData} options={options} style={{ maxHeight: "100%", maxWidth: "100%" }} />
    </div>
  );
};
